import { cva, type VariantProps } from 'class-variance-authority';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cn } from '../lib/cn';
import { FIELD_BORDER } from '../lib/recipes';

import { Spinner } from './spinner';

/**
 * The funnel's `.btn-submit` is the primary (tokens doc §7.1): 12px radius — rounder
 * than the 10px input — 16/600 type, a solid blue fill, and elevation only on hover.
 * `secondary` is derived from the same geometry with the selectable-surface treatment
 * (§7.2); `destructive` is an admin-only addition (§10.4).
 *
 * Q-P15 (2026-09-16, closes KI-03): the fill is `--color-primary-accessible`, not
 * `--color-primary`. A button label is white text on that fill, and `--color-primary` is
 * 2.40:1 under white — it was the single largest source of the 102 serious axe nodes
 * left after M13.7. The brand blue keeps every fill that carries nothing: headers, hero
 * bands, progress bars, the nav rail. `--shadow-primary` stays the brand blue too: it is
 * a glow behind the button, not the surface under the label.
 *
 * The same reasoning moves `destructive` off `--color-danger` (3.76:1) onto
 * `--color-danger-accessible` (4.83:1). See the rule at the top of `theme.css`.
 */
const button = cva(
  [
    'inline-flex items-center justify-center gap-gutter',
    'rounded-button font-semibold whitespace-nowrap',
    'cursor-pointer select-none',
    'transition-[background-color,border-color,box-shadow,transform] duration-200',
    'active:scale-[0.99]',
    'disabled:cursor-not-allowed disabled:shadow-none disabled:active:scale-100',
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-primary-accessible text-primary-contrast',
          'hover:bg-primary-accessible-hover hover:shadow-primary',
          'disabled:bg-neutral-300 disabled:hover:bg-neutral-300 disabled:hover:shadow-none',
        ],
        secondary: [
          FIELD_BORDER,
          'bg-surface text-primary-accessible',
          'hover:border-primary-hover',
          'disabled:border-neutral-200 disabled:text-neutral-400-text',
        ],
        ghost: [
          'bg-transparent text-primary-accessible',
          'hover:bg-primary-subtle',
          'disabled:text-neutral-400-text disabled:hover:bg-transparent',
        ],
        destructive: [
          'bg-danger-accessible text-primary-contrast',
          'hover:bg-danger-accessible-hover',
          'disabled:bg-neutral-300 disabled:hover:bg-neutral-300',
        ],
      },
      /*
       * `max-lg:min-h-11` (M11.11, rules §49.3): below 1024px — the width at which the
       * admin shell switches to its drawer layout and a thumb, not a mouse, is the
       * pointer — every button is at least 44px tall. The padding is unchanged, so the
       * desktop geometry quoted from the funnel stays exact and only the touch layout
       * grows: designed for the size, not a compressed desktop (NFR-07).
       */
      size: {
        sm: 'px-3 py-[6px] text-sm max-lg:min-h-11',
        md: 'px-4 py-[10px] text-base max-lg:min-h-11',
        lg: 'px-6 py-4 text-lg',
      },
      block: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md', block: false },
  },
);

export type ButtonVariant = NonNullable<VariantProps<typeof button>['variant']>;
export type ButtonSize = NonNullable<VariantProps<typeof button>['size']>;

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'>, VariantProps<typeof button> {
  /** Shows a spinner, disables the button and sets `aria-busy` (rules §17.7). */
  loading?: boolean;
  /** Announced while `loading`, so the change of state is not conveyed by the spinner alone. */
  loadingLabel?: string;
  /** Rendered before the label. Use a lucide icon at `strokeWidth={2}`. */
  icon?: ReactNode;
  children: ReactNode;
}

/**
 * The one button. A native `<button>`, so keyboard and screen-reader behaviour is free
 * (rules §48.2). `type` defaults to "button" so a button inside a form never submits it
 * by accident; pass `type="submit"` deliberately.
 */
export function Button({
  variant,
  size,
  block,
  loading = false,
  loadingLabel = 'Working',
  icon,
  className,
  type = 'button',
  disabled,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn(button({ variant, size, block }), className)}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner size={size === 'lg' ? 18 : 16} label={loadingLabel} /> : (icon ?? null)}
      {children}
    </button>
  );
}
